import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Check, ChevronDown, RefreshCw } from "lucide-react";
import { type GroupKpiItem } from "./grcGroupData";

export interface ScopeItem { id: string; name: string }

/* ── Themed card (app design tokens) ────────────────────────────────── */
export function Card({
  children,
  className = "",
  id,
  name,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  name?: string;
}) {
  return (
    <div id={id} data-name={name} className={`rounded-xl border border-border bg-card p-5 pt-3 ${className}`}>
      {children}
    </div>
  );
}

/* ── Loading / empty placeholder for a chart card ───────────────────── */
export function EmptyState({ loading }: { loading: boolean; label?: string }) {
  return (
    <div className="flex h-full min-h-[160px] w-full flex-col items-center justify-center py-10 text-center">
      {loading ? (
        <RefreshCw className="size-5 animate-spin text-muted-foreground" />
      ) : (
        <span className="text-sm font-bold text-[#1e3a8a] dark:text-blue-300">No Data Available</span>
      )}
    </div>
  );
}

export function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

/* ── Group picker (driven by /filter-scope) ─────────────────────────── */
export function GroupPicker({
  items,
  selectedId,
  selectedName,
  onSelect,
  label = "Group",
  placeholder,
  emptyText,
}: {
  items: ScopeItem[];
  selectedId: string | null;
  selectedName: string;
  onSelect: (it: ScopeItem) => void;
  label?: string;
  placeholder?: string;
  emptyText?: string;
}) {
  const [open, setOpen] = useState(false);
  // Draft checkbox selection — only committed (via onSelect) when "Filter" is
  // clicked, so checking an item doesn't change scope/refetch immediately.
  const [draftId, setDraftId] = useState(selectedId);
  const lower = label.toLowerCase();

  useEffect(() => setDraftId(selectedId), [selectedId]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground/70 transition-colors hover:border-indigo-300 dark:hover:border-indigo-500/40"
      >
        <span className="text-muted-foreground">{label}</span>
        <span className="max-w-[180px] truncate text-foreground">
          {selectedName || placeholder || `Select ${lower}`}
        </span>
        <ChevronDown
          className={`size-3.5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1.5 max-h-80 w-60 overflow-hidden rounded-lg border border-border bg-popover shadow-xl">
            {items.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                {emptyText || `No ${lower}s available`}
              </p>
            ) : (
              <>
                <div className="max-h-64 overflow-y-auto py-1">
                  {items.map((it) => {
                    const checked = it.id === draftId;
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setDraftId(it.id)}
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                          checked ? "font-semibold text-primary" : "text-foreground/80"
                        }`}
                      >
                        <span
                          className={`flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                            checked
                              ? "border-indigo-500 bg-indigo-500 text-white"
                              : "border-slate-400 dark:border-white/40"
                          }`}
                        >
                          {checked && <Check className="size-3" />}
                        </span>
                        <span className="truncate">{it.name}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="border-t border-border p-2">
                  <button
                    type="button"
                    onClick={() => {
                      const chosen = items.find((it) => it.id === draftId);
                      if (chosen) onSelect(chosen);
                      setOpen(false);
                    }}
                    className="w-full rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
                  >
                    Filter
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ── KPI card ────────────────────────────────────────────────────────── */
export function GroupKpiCard({ kpi, index }: { kpi: GroupKpiItem; index: number }) {
  const Icon = kpi.icon;
  /* 0 / blank / non-numeric ("—") → no data: show a centred message instead. */
  const num = typeof kpi.value === "number" ? kpi.value : parseInt(String(kpi.value));
  const noData = !num || Number.isNaN(num);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: { duration: 0.4, delay: index * 0.07, ease: "easeOut" },
      }}
      transition={{ duration: 0.12, ease: "easeOut" }}
      whileHover={{
        y: -3,
        boxShadow: `0 8px 22px -12px ${kpi.color}55`,
        borderColor: `${kpi.color}40`,
      }}
      className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card p-3"
    >
      {/* left side accent (hidden in the Extra-cards selector via .kpi-accent) */}
      <span
        className="kpi-accent pointer-events-none absolute inset-y-0 left-0 w-1 transition-all duration-300 group-hover:w-1.5"
        style={{ background: kpi.color }}
      />

      {/* label + icon — pinned to the top, fixed height so every card aligns */}
      <div className="flex min-h-[2rem] items-start justify-between gap-2">
        <p className="whitespace-nowrap text-sm font-semibold leading-tight tracking-tight text-foreground">
          {kpi.label}
        </p>
        <div
          className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg transition-transform duration-300 group-hover:scale-110"
          style={{ background: `${kpi.color}1f` }}
        >
          <Icon className="size-4" style={{ color: kpi.color }} />
        </div>
      </div>

      {/* value zone — always bottom-left so the whole row shares one baseline */}
      <div className="mt-auto flex flex-col gap-0.5 pt-1">
        {noData ? (
          <>
            {/* a deliberate placeholder dash keeps the empty card looking intentional */}
            <span
              className="inline-block h-1 w-5 rounded-full bg-muted-foreground/25"
              aria-hidden
            />
            <p className="text-xs font-semibold leading-tight text-muted-foreground/70">
              No data available
            </p>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className="whitespace-nowrap break-normal text-2xl font-bold tabular-nums leading-none text-foreground">
                {kpi.value}
              </span>
              {kpi.catalog && <CatalogBadge label={kpi.catalog} subtle />}
            </div>
            {/* subtext */}
            {kpi.delta && (
              <p className="text-[11px] leading-tight text-muted-foreground">{kpi.delta}</p>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}

/* ── Catalog / framework badge ──────────────────────────────────────── */
function CatalogBadge({ label, subtle = false }: { label: string; subtle?: boolean }) {
  return (
    <span
      title={`Measured against the ${label} catalog`}
      className={
        subtle
          ? "inline-flex items-center rounded bg-primary/[0.08] px-1.5 py-px text-[9px] font-bold leading-none tracking-wide text-primary/80 ring-1 ring-inset ring-primary/15"
          : "inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold leading-none tracking-wide text-primary"
      }
    >
      {label}
    </span>
  );
}
